import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Apple,
  Bell,
  Fingerprint,
  Film,
  Home,
  List,
  Loader2,
  Radio,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Siren,
  Smartphone,
  Video,
  Wifi,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CreatePreReservaRequest } from '@visep/shared-types/presupuestos';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/use-form-submit';
import { glassInput, glassLabel } from '@/components/landing/glass';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const FEATURES = [
  { icon: Video, label: 'Cámaras en vivo' },
  { icon: ShieldCheck, label: 'Control de alarma' },
  { icon: Bell, label: 'Notificaciones push' },
  { icon: Siren, label: 'Botón SOS' },
  { icon: Film, label: 'Grabaciones' },
  { icon: Fingerprint, label: 'Biometría' },
] as const;

const TIPO_OPTIONS = [
  'Residencia / Casa',
  'Empresa / Oficina',
  'Comercio / Local',
  'Estancia / Campo',
  'Industria / Planta',
  'Otro',
] as const;

const TAB_BAR = [
  { icon: Home, label: 'Inicio', active: true },
  { icon: Video, label: 'Cámaras', active: false },
  { icon: List, label: 'Historial', active: false },
  { icon: Bell, label: 'Alertas', active: false, badge: 3 },
  { icon: Settings, label: 'Config', active: false },
] as const;

interface ReserveForm {
  nombre: string;
  email: string;
  whatsapp: string;
  ubicacion: string;
  tipo: string;
}

const EMPTY: ReserveForm = { nombre: '', email: '', whatsapp: '', ubicacion: '', tipo: '' };

function PhoneMockup() {
  return (
    <div className="relative">
      <div className="absolute inset-[-50px] rounded-full bg-emerald-500/[0.03] blur-3xl" />
      <div className="relative h-[570px] w-[270px] rounded-[3rem] border border-slate-600/30 bg-gradient-to-b from-[#1C1C1E] to-[#0C0C0E] p-[3px] shadow-[0_24px_80px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)]">
        <div className="relative h-full w-full overflow-hidden rounded-[2.8rem] bg-black">
          {/* Dynamic Island */}
          <div className="absolute left-1/2 top-3 z-30 h-[28px] w-[90px] -translate-x-1/2 rounded-full border border-slate-800/50 bg-black" />
          <div className="flex h-full w-full flex-col overflow-hidden bg-[#0C0C0C] px-3.5 pb-2 pt-14">
            {/* Status bar */}
            <div className="mb-3 flex items-center justify-between px-0.5 text-[10px] text-slate-500">
              <span className="font-medium">9:41</span>
              <div className="flex items-center gap-1.5">
                <div className="flex items-end gap-[1px]">
                  {[3, 4, 5, 6].map((h) => (
                    <div key={h} className="w-[2.5px] rounded-sm bg-white/50" style={{ height: h }} />
                  ))}
                </div>
                <Wifi className="h-3.5 w-3.5 text-white/50" aria-hidden />
                <div className="relative h-3 w-6 rounded-sm border border-white/30">
                  <div className="absolute inset-[1.5px] right-[3px] rounded-[1px] bg-emerald-400" />
                  <div className="absolute -right-[2px] top-1/2 h-[5px] w-[1.5px] -translate-y-1/2 rounded-r-full bg-white/30" />
                </div>
              </div>
            </div>
            {/* App header */}
            <div className="mb-3 flex items-start justify-between px-0.5">
              <div>
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-primary">
                  VISEP Smart Security
                </div>
                <div className="mt-0.5 text-[16px] font-bold tracking-tight text-white">
                  Panel de Control
                </div>
              </div>
              <div className="relative">
                <Bell className="h-5 w-5 text-primary" strokeWidth={1.5} aria-hidden />
                <div className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500">
                  <span className="text-[7px] font-bold text-white">3</span>
                </div>
              </div>
            </div>
            {/* Sistema armado */}
            <div className="mb-2.5 rounded-2xl border border-emerald-600/20 bg-emerald-900/30 p-3">
              <div className="mb-1.5 flex items-start gap-1.5">
                <div className="mt-1 h-2 w-2 animate-[status-pulse_2s_ease-in-out_infinite] rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)] motion-reduce:animate-none" />
                <div>
                  <div className="text-[12px] font-bold text-emerald-400">Sistema Armado</div>
                  <div className="text-[9px] text-emerald-600">Protección total activa</div>
                </div>
                <ShieldCheck
                  className="-mt-1 ml-auto h-7 w-7 text-emerald-500/60"
                  strokeWidth={1.5}
                  aria-hidden
                />
              </div>
            </div>
            {/* Modos */}
            <div className="mb-2.5 grid grid-cols-3 gap-1.5">
              <div className="rounded-xl border-2 border-primary/40 bg-white/[0.04] p-2 text-center">
                <ShieldCheck className="mx-auto mb-0.5 h-4 w-4 text-primary" strokeWidth={1.5} aria-hidden />
                <div className="text-[9px] font-semibold text-primary">Total</div>
                <div className="mx-auto mt-1 h-[2px] w-6 rounded-full bg-primary" />
              </div>
              <div className="rounded-xl border border-white/[0.04] bg-white/[0.03] p-2 text-center">
                <Shield className="mx-auto mb-0.5 h-4 w-4 text-slate-500" strokeWidth={1.5} aria-hidden />
                <div className="text-[9px] text-slate-500">Desarmar</div>
              </div>
              <div className="rounded-xl border border-white/[0.04] bg-white/[0.03] p-2 text-center">
                <Shield className="mx-auto mb-0.5 h-4 w-4 text-slate-600" strokeWidth={1.5} aria-hidden />
                <div className="text-[9px] text-slate-600">Parcial</div>
              </div>
            </div>
            {/* Stats */}
            <div className="mb-2.5 grid grid-cols-3 gap-1.5">
              <div className="rounded-xl border border-white/[0.04] bg-white/[0.03] p-2 text-center">
                <Video className="mx-auto mb-0.5 h-3.5 w-3.5 text-blue-400" strokeWidth={1.5} aria-hidden />
                <div className="text-[11px] font-bold text-white">3/4</div>
                <div className="text-[7px] text-slate-500">Cámaras</div>
              </div>
              <div className="rounded-xl border border-white/[0.04] bg-white/[0.03] p-2 text-center">
                <Radio className="mx-auto mb-0.5 h-3.5 w-3.5 text-emerald-400" strokeWidth={1.5} aria-hidden />
                <div className="text-[11px] font-bold text-white">6</div>
                <div className="text-[7px] text-slate-500">Sensores</div>
              </div>
              <div className="rounded-xl border border-white/[0.04] bg-white/[0.03] p-2 text-center">
                <AlertTriangle className="mx-auto mb-0.5 h-3.5 w-3.5 text-red-400" strokeWidth={1.5} aria-hidden />
                <div className="text-[11px] font-bold text-white">2</div>
                <div className="text-[7px] text-slate-500">Alertas</div>
              </div>
            </div>
            {/* Cámaras en vivo */}
            <div className="mb-1.5 flex items-center justify-between px-0.5">
              <div className="text-[10px] font-bold text-white">Cámaras en vivo</div>
              <div className="text-[8px] text-primary">Ver todas</div>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-1.5">
              {[
                { name: 'Entrada principal', zone: 'Exterior Norte' },
                { name: 'Salón', zone: 'Interior' },
              ].map((cam) => (
                <div
                  key={cam.name}
                  className="overflow-hidden rounded-xl border border-white/[0.04] bg-slate-900/80"
                >
                  <div className="relative flex h-[52px] items-start bg-gradient-to-br from-slate-800/50 to-slate-900/80 p-1.5">
                    <div className="flex items-center gap-0.5 rounded border border-emerald-500/30 bg-emerald-500/20 px-1.5 py-0.5 text-[6px] font-bold text-emerald-400">
                      <div className="h-1 w-1 rounded-full bg-emerald-400" />
                      EN VIVO
                    </div>
                  </div>
                  <div className="p-1.5 pt-1">
                    <div className="text-[9px] font-semibold text-white">{cam.name}</div>
                    <div className="text-[7px] text-slate-600">{cam.zone}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* Tab bar */}
            <div className="mt-auto">
              <div className="flex items-center justify-around border-t border-white/[0.04] py-1.5">
                {TAB_BAR.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <div key={tab.label} className="relative flex flex-col items-center gap-0.5">
                      {tab.active && (
                        <div className="absolute -top-1.5 h-1 w-1 rounded-full bg-primary" />
                      )}
                      <Icon
                        className={`h-4 w-4 ${tab.active ? 'text-primary' : 'text-slate-600'}`}
                        strokeWidth={1.5}
                        aria-hidden
                      />
                      <span className={`text-[7px] ${tab.active ? 'text-primary' : 'text-slate-600'}`}>
                        {tab.label}
                      </span>
                      {'badge' in tab && (
                        <div className="absolute -right-1.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-red-500">
                          <span className="text-[6px] font-bold text-white">{tab.badge}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-1.5 flex justify-center">
                <div className="h-[4px] w-[80px] rounded-full bg-white/10" />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute right-0 top-8 h-24 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />
      </div>
    </div>
  );
}

export function AppMockup() {
  const [form, setForm] = useState<ReserveForm>(EMPTY);
  const [honeypot, setHoneypot] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { status, errorMessage, submit } = useFormSubmit((input: CreatePreReservaRequest) =>
    api.preReservas.create(input),
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!form.nombre.trim() || !form.email.trim()) {
      setLocalError('Ingresá tu nombre y correo electrónico.');
      return;
    }
    const preferencia = [
      form.tipo ? `Tipo: ${form.tipo}` : '',
      form.ubicacion.trim() ? `Ubicación: ${form.ubicacion.trim()}` : '',
    ]
      .filter(Boolean)
      .join('. ')
      .slice(0, 500);
    const input: CreatePreReservaRequest = {
      nombre: form.nombre.trim(),
      email: form.email.trim(),
      ...(form.whatsapp.trim() ? { telefono: form.whatsapp.trim() } : {}),
      ...(preferencia ? { preferencia } : {}),
    };
    const ok = await submit(input, honeypot);
    if (ok) {
      toast.success('¡Lugar reservado! Te avisaremos cuando la VISEP App esté disponible.');
      setForm(EMPTY);
    }
  }

  return (
    <section
      id="app"
      className="relative scroll-mt-16 overflow-hidden border-y border-white/[0.04] py-20 lg:py-32"
    >
      <div className="container relative z-10">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="order-2 flex justify-center lg:order-1"
            >
              <PhoneMockup />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="order-1 lg:order-2"
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 font-mono text-xs text-slate-400">
                Próximamente
              </div>
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-white lg:text-5xl">
                VISEP App.
              </h2>
              <p className="mb-10 max-w-md text-lg leading-relaxed text-slate-400">
                Control total de su seguridad desde su celular. Cámaras en vivo, control de alarma,
                notificaciones instantáneas y botón de emergencia.
              </p>
              <ul className="mb-10 grid max-w-sm grid-cols-2 gap-x-6 gap-y-4">
                {FEATURES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <li key={f.label} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Icon className="h-4 w-4 shrink-0 text-slate-500" strokeWidth={1.5} aria-hidden />
                      {f.label}
                    </li>
                  );
                })}
              </ul>
              <div className="flex gap-3">
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-2.5 text-sm text-slate-300">
                  <Apple className="h-4 w-4" aria-hidden /> iOS
                </span>
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-2.5 text-sm text-slate-300">
                  <Smartphone className="h-4 w-4" aria-hidden /> Android
                </span>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto mt-20 max-w-2xl"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-2xl font-bold tracking-tight text-white">Reserve su lugar</h3>
              <p className="text-sm text-slate-400">
                Sea de los primeros en acceder a VISEP App. Dejenos sus datos y lo contactaremos.
              </p>
            </div>
            <form
              onSubmit={handleSubmit}
              noValidate
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="reserve-nombre" className={glassLabel}>
                    Nombre completo
                  </Label>
                  <Input
                    id="reserve-nombre"
                    className={glassInput}
                    name="nombre"
                    autoComplete="name"
                    placeholder="Su nombre"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="reserve-email" className={glassLabel}>
                    Correo electrónico
                  </Label>
                  <Input
                    id="reserve-email"
                    className={glassInput}
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="su@correo.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="reserve-whatsapp" className={glassLabel}>
                    WhatsApp
                  </Label>
                  <Input
                    id="reserve-whatsapp"
                    className={glassInput}
                    name="whatsapp"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+595 9XX XXX XXX"
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="reserve-ubicacion" className={glassLabel}>
                    Ubicación
                  </Label>
                  <Input
                    id="reserve-ubicacion"
                    className={glassInput}
                    name="ubicacion"
                    placeholder="Ciudad / Zona"
                    value={form.ubicacion}
                    onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                  />
                </div>
                <div className="grid gap-2 md:col-span-2">
                  <Label htmlFor="reserve-tipo" className={glassLabel}>
                    Tipo de propiedad
                  </Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger id="reserve-tipo" name="tipo" className={glassInput}>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPO_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Honeypot anti-spam */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
              {(localError || errorMessage) && (
                <p className="mt-4 text-sm text-error" role="alert">
                  {localError ?? errorMessage}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={status === 'loading'}
                className="mt-6 w-full rounded-xl"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Reservando…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden /> Reservar Acceso Anticipado
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
